import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useEquinorFlag } from 'utils/featureFlags';
import {
  EQUINOR_GRANE_EVENT_REGION,
  EQUINOR_GRANE_EVENT_STARTED_AT,
  EQUINOR_GRANE_EVENT_SUMMARY,
  EQUINOR_GRANE_FACILITIES,
  EQUINOR_GRANE_LINKED_EVENT_NAME,
  EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  EQUINOR_GRANE_RESOURCES_COMMITTED,
  EQUINOR_GRANE_THRESHOLD_DESCRIPTION,
  type EquinorGraneFacility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/equinorGraneSpillNotification';
import { orgEventKeys, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';

const EQUINOR_EVENT_TYPE = 'Oil Spill';

const ensureLocks = new Map<string, Promise<unknown>>();

function equinorCentroid(sites: EquinorGraneFacility[]): {
  lon: number;
  lat: number;
} {
  if (sites.length === 0) {
    // Midway between Grane and Utsira, so the event still lands in the demo area.
    return { lon: 3.7, lat: 59.24 };
  }
  const lon = sites.reduce((sum, f) => sum + f.lon, 0) / sites.length;
  const lat = sites.reduce((sum, f) => sum + f.lat, 0) / sites.length;
  return { lon, lat };
}

async function patchEquinorOrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const needsPatch =
    existing.name !== EQUINOR_GRANE_LINKED_EVENT_NAME ||
    existing.summary !== EQUINOR_GRANE_EVENT_SUMMARY ||
    existing.resources_committed !== EQUINOR_GRANE_RESOURCES_COMMITTED ||
    existing.region !== EQUINOR_GRANE_EVENT_REGION ||
    existing.type !== EQUINOR_EVENT_TYPE ||
    existing.threshold_description !== EQUINOR_GRANE_THRESHOLD_DESCRIPTION;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: EQUINOR_GRANE_LINKED_EVENT_NAME,
      summary: EQUINOR_GRANE_EVENT_SUMMARY,
      resources_committed: EQUINOR_GRANE_RESOURCES_COMMITTED,
      region: EQUINOR_GRANE_EVENT_REGION,
      type: EQUINOR_EVENT_TYPE,
      threshold_description: EQUINOR_GRANE_THRESHOLD_DESCRIPTION,
    }
  );
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(existing.group_guid),
  });
  return patched.data;
}

async function ensureEquinorOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === EQUINOR_GRANE_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchEquinorOrgEventIfStale(existing, queryClient);
  }

  const { lon, lat } = equinorCentroid(EQUINOR_GRANE_FACILITIES);
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: EQUINOR_GRANE_LINKED_EVENT_NAME,
    type: EQUINOR_EVENT_TYPE,
    status: 'Active',
    severity: 'High',
    region: EQUINOR_GRANE_EVENT_REGION,
    business_unit: 'Equinor',
    summary: EQUINOR_GRANE_EVENT_SUMMARY,
    resources_committed: EQUINOR_GRANE_RESOURCES_COMMITTED,
    lon,
    lat,
    started_at: EQUINOR_GRANE_EVENT_STARTED_AT,
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: EQUINOR_GRANE_THRESHOLD_DESCRIPTION,
    demo_key: EQUINOR_GRANE_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

export function useEnsureEquinorGraneOrgEvent() {
  const equinorEnabled = useEquinorFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!equinorEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `equinor-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureEquinorOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Equinor Grane org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [equinorEnabled, groupGuid, userGuid, queryClient]);
}
